import React from 'react';
import { PublicLayout } from '../components/PublicLayout';
import { PageHero, BenefitsGrid, FAQSection, CTASection, FeatureList } from '../components/PageTemplate';
import { 
  Building2, Users, Shield, Zap, BarChart, Clock, 
  CheckCircle, Briefcase, FileText, MessageCircle 
} from 'lucide-react';

export default function AgentsPage() {
  const benefits = [
    {
      icon: Building2,
      title: 'Multi-Property Dashboard',
      description: 'Manage all your client properties from one screen. Filter by landlord, status, or issue.'
    },
    {
      icon: Users,
      title: 'Client Management',
      description: 'Onboard landlords, manage permissions, and handle their portfolios efficiently.'
    },
    {
      icon: Shield,
      title: 'Compliance Automation',
      description: 'Track certificates across all properties. Automatic reminders mean nothing slips.'
    },
    {
      icon: Zap,
      title: 'Streamlined Operations',
      description: 'Tenant applications, referencing, maintenance - all automated with your oversight.'
    },
    {
      icon: BarChart,
      title: 'Reporting & Analytics',
      description: 'Portfolio performance, void periods, rent collection rates - all at your fingertips.'
    },
    {
      icon: Clock,
      title: 'Time Savings',
      description: 'Automation handles the repetitive work. Focus on client relationships and growth.'
    },
    {
      icon: FileText,
      title: 'White-Label Options',
      description: 'Brand the tenant portal with your agency name for a seamless client experience.'
    },
    {
      icon: MessageCircle,
      title: 'Centralised Communication',
      description: 'All landlord and tenant communication in one place. Full audit trail.'
    }
  ];

  const features = [
    'Manage unlimited properties and landlords',
    'Automated compliance tracking and reminders',
    'Streamlined tenant applications and referencing',
    'Integrated maintenance workflow',
    'Contractor network access',
    'Rent collection and arrears management',
    'Inspection scheduling and reporting',
    'Financial reporting and statements',
    'White-label tenant portal (optional)',
    'API access for integrations',
    'Dedicated account manager',
    'Priority support'
  ];

  const faqs = [
    {
      question: 'How is pricing structured for agents?',
      answer: 'We offer volume-based pricing. Contact us for a quote based on your portfolio size. Generally, agents pay a reduced per-property fee compared to individual landlords.'
    },
    {
      question: 'Can I white-label the platform?',
      answer: 'Yes. On our Professional plan, tenant portals can be branded with your agency name, logo, and colours. Landlords still access via LetWise.'
    },
    {
      question: 'How do I onboard my existing properties?',
      answer: 'We provide a bulk import tool and dedicated onboarding support. Most agencies are fully migrated within 2-4 weeks depending on portfolio size.'
    },
    {
      question: 'Does LetWise integrate with other systems?',
      answer: 'We offer API access on Professional plans. Common integrations include accounting software, CRMs, and portal feeds.'
    },
    {
      question: 'What support do agents receive?',
      answer: 'All agents get a dedicated account manager, priority support queue, and access to training resources. Professional plans include custom onboarding.'
    },
    {
      question: 'Can landlords access their properties directly?',
      answer: 'You control the permissions. Landlords can have view-only access, full access, or no direct access - it\'s up to you and your service model.'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="FOR LETTING AGENTS"
        title="Scale Your Agency"
        subtitle="With Better Technology"
        description="Manage multiple landlords and properties efficiently. Automate compliance, streamline operations, and deliver better service."
        primaryCTA={{ href: '/contact', label: 'Talk to Us' }}
        secondaryCTA={{ href: '/landlords/pricing', label: 'View Pricing' }}
        gradient="from-purple-50 via-white to-slate-50"
      />

      <BenefitsGrid title="Why Agents Choose LetWise" benefits={benefits} />

      {/* Features List */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8">
            Everything You Need
          </h2>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            <FeatureList features={features.slice(0, 6)} />
            <FeatureList features={features.slice(6)} />
          </div>
        </div>
      </section>

      {/* Plans Overview */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-4">
            Agent Plans
          </h2>
          <p className="text-center text-slate-600 mb-8 max-w-2xl mx-auto">
            Pricing scales with your portfolio. Contact us for a custom quote.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 border">
              <h3 className="font-bold text-lg mb-2">Starter</h3>
              <p className="text-sm text-slate-600 mb-4">Up to 50 properties</p>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Full platform access</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Compliance automation</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Standard support</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border-2 border-primary">
              <div className="text-xs font-semibold text-primary mb-2">MOST POPULAR</div>
              <h3 className="font-bold text-lg mb-2">Professional</h3>
              <p className="text-sm text-slate-600 mb-4">50-250 properties</p>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Everything in Starter</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> White-label portal</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> API access</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Dedicated account manager</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border">
              <h3 className="font-bold text-lg mb-2">Enterprise</h3>
              <p className="text-sm text-slate-600 mb-4">250+ properties</p>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Everything in Professional</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Custom integrations</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> SLA guarantees</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Onboarding support</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <CTASection
        title="Ready to modernise your agency?"
        description="Talk to our team about how LetWise can help you scale."
        primaryCTA={{ href: '/contact', label: 'Get in Touch' }}
        secondaryCTA={{ href: '/register', label: 'Try It Free' }}
      />
    </PublicLayout>
  );
}
